import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import Layout from "../common/Layout";
import { useAuth } from "../../context/AuthContext";
import Select from "react-select";
import { getBusinesses } from "../../api/businessApi";
import { getTerminals } from "../../api/terminalApi";
import { getDashboard } from "../../api/dashboardApi";
import { parseDateTime } from "@internationalized/date";

import { DateRangePicker } from "../common/DateRangePicker";

// ---------- normalize text ----------
const normalizeText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// ---------- deep search ----------
const buildHaystack = (obj, depth = 0, seen = new WeakSet()) => {
  if (obj == null || depth > 12) return "";
  const t = typeof obj;

  if (t === "string") return obj;
  if (t === "number" || t === "boolean" || t === "bigint") return String(obj);
  if (t === "function" || t === "symbol") return "";

  if (t === "object") {
    if (obj instanceof Date) return obj.toISOString();

    if (seen.has(obj)) return "";
    seen.add(obj);

    if (Array.isArray(obj)) {
      let out = "";
      for (let i = 0; i < obj.length; i++) {
        try {
          out += " " + buildHaystack(obj[i], depth + 1, seen);
        } catch {
          /* skip */
        }
      }
      return out;
    }

    let out = "";
    let keys = [];
    try {
      keys = Object.keys(obj);
    } catch {
      return "";
    }

    for (const k of keys) {
      out += " " + k;
      try {
        out += " " + buildHaystack(obj[k], depth + 1, seen);
      } catch {
        /* skip */
      }
    }
    return out;
  }
  return "";
};

// ---------- JS Date -> CalendarDateTime (for the picker) ----------
const dateToCalendarDateTime = (date) => {
  if (!date) return null;
  const pad = (n) => String(n).padStart(2, "0");
  const str = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds(),
  )}`;
  return parseDateTime(str);
};

// ---------- CalendarDateTime -> JS Date ----------
const calendarDateTimeToDate = (cdt) => {
  if (!cdt) return null;
  return new Date(
    cdt.year,
    cdt.month - 1,
    cdt.day,
    cdt.hour ?? 0,
    cdt.minute ?? 0,
    cdt.second ?? 0,
    cdt.millisecond ?? 0,
  );
};

// ---------- local ISO WITHOUT timezone suffix ----------
// Sends raw local wall-clock time e.g. "2025-01-15T09:30:00"
// No "Z", no offset — backend treats this as local time.
const toLocalIso = (date) => {
  if (!date) return null;
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const mi = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
};

// ---------- LOCAL time helpers (browser timezone) ----------
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

// Preset range calculator — uses LOCAL time only
const computePresetRange = (preset) => {
  const now = new Date();
  let from = new Date(now);
  let to = new Date(now);

  switch (preset) {
    case "today":
      break;
    case "yesterday":
      from.setDate(now.getDate() - 1);
      to.setDate(now.getDate() - 1);
      break;
    case "thisWeek": {
      const day = now.getDay(); // 0 = Sun, 1 = Mon, ...
      const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday start
      from = new Date(now.getFullYear(), now.getMonth(), diff);
      to = new Date(now);
      break;
    }
    case "thisMonth":
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to = new Date(now);
      break;
    case "thisYear":
      from = new Date(now.getFullYear(), 0, 1);
      to = new Date(now);
      break;
    default:
      return null;
  }

  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);
  return { from, to };
};

// ---------- component ----------
const Dashboard = () => {
  const { logout } = useAuth();
  const [activeMenu, setActiveMenu] = useState("dashboard");

  const [dateRange, setDateRange] = useState("today");
  const [startDate, setStartDate] = useState(() => startOfToday());
  const [endDate, setEndDate] = useState(() => endOfToday());
  const [business, setBusiness] = useState(null);
  const [terminal, setTerminal] = useState(null);
  const [dateError, setDateError] = useState("");

  const [businessOptions, setBusinessOptions] = useState([]);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [terminalOptions, setTerminalOptions] = useState([]);
  const [terminalLoading, setTerminalLoading] = useState(false);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const requestIdRef = useRef(0);
  const businessesLoadedRef = useRef(false);
  const businessesLoadingRef = useRef(false);

  const rangeOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "thisWeek", label: "This Week" },
    { value: "thisMonth", label: "This Month" },
    { value: "thisYear", label: "This Year" },
    { value: "customDate", label: "Custom Range" },
  ];

  const fmt = (n) =>
    typeof n === "number"
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  const fmtInt = (n) => (typeof n === "number" ? n.toLocaleString() : "0");

  // ---------- Load businesses on mount ----------
  useEffect(() => {
    // Once-only guard — prevents duplicate calls (StrictMode, remounts, etc.)
    if (businessesLoadedRef.current || businessesLoadingRef.current) return;

    let cancelled = false;
    businessesLoadingRef.current = true;

    const loadBusinesses = async () => {
      setBusinessLoading(true);
      try {
        const list = await getBusinesses();
        if (cancelled) return;

        const arr = Array.isArray(list)
          ? list
          : list?.data || list?.items || [];

        const options = arr.map((b) => {
          const guid =
            b.businessGuid ||
            b.BusinessGuid ||
            b.business?.businessGuid ||
            b.business?.BusinessGuid ||
            b.id ||
            b.Id;

          const label =
            b.tradingName ||
            b.TradingName ||
            b.companyName ||
            b.CompanyName ||
            "Unnamed Business";

          let deep = "";
          try {
            deep = buildHaystack(b);
          } catch (e) {
            console.warn("Haystack build failed for business:", guid, e);
          }

          const explicit = [
            b.companyName,
            b.CompanyName,
            b.tradingName,
            b.TradingName,
            b.email,
            b.Email,
            b.emailAddress,
            b.EmailAddress,
            b.phone,
            b.Phone,
            b.phoneNumber,
            b.PhoneNumber,
            b.mobile,
            b.Mobile,
            b.mobileNumber,
            b.MobileNumber,
            b.address,
            b.Address,
            b.addressLine1,
            b.AddressLine1,
            b.addressLine2,
            b.AddressLine2,
            b.street,
            b.Street,
            b.suburb,
            b.Suburb,
            b.city,
            b.City,
            b.state,
            b.State,
            b.postcode,
            b.Postcode,
            b.zip,
            b.Zip,
            b.country,
            b.Country,
            b.acquirerMId,
            b.AcquirerMId,
            b.businessGuid,
            b.BusinessGuid,
            guid,
            label,
          ]
            .filter(Boolean)
            .join(" ");

          const haystack = normalizeText(`${explicit} ${deep}`);

          return {
            value: guid,
            label,
            business: b,
            __haystack: haystack,
          };
        });

        setBusinessOptions(options);
        businessesLoadedRef.current = true;
      } catch (err) {
        if (!cancelled) {
          setError("Failed to load business list.");
        }
      } finally {
        if (!cancelled) setBusinessLoading(false);
        businessesLoadingRef.current = false;
      }
    };

    loadBusinesses();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- Load terminals when business changes ----------
  useEffect(() => {
    let cancelled = false;

    if (!business) {
      setTerminalOptions([]);
      setTerminal(null);
      return;
    }

    const businessId = business.value || business.business?.businessGuid;
    if (!businessId) {
      setTerminalOptions([]);
      return;
    }

    const loadTerminals = async () => {
      setTerminalLoading(true);
      try {
        const list = await getTerminals(businessId);
        if (cancelled) return;

        const arr = Array.isArray(list)
          ? list
          : list?.data || list?.items || [];

        const options = arr.map((t, i) => {
          const id =
            t.terminalGuid ||
            t.terminalId ||
            t.acquirerTId ||
            t.id ||
            `terminal-${i}`;
          const label =
            t.tradingName ||
            t.terminalName ||
            t.name ||
            t.acquirerTId ||
            `Terminal ${i + 1}`;

          return {
            value: id,
            label,
            terminal: t,
          };
        });

        setTerminalOptions(options);
      } catch (err) {
        if (!cancelled) {
          setTerminalOptions([]);
        }
      } finally {
        if (!cancelled) setTerminalLoading(false);
      }
    };
    loadTerminals();
    return () => {
      cancelled = true;
    };
  }, [business]);

  // ---------- handlers ----------
  const handleMenuChange = (menu) => {
    setActiveMenu(menu);
    if (menu === "logout") logout();
  };

  // When user picks a preset range (Today / Yesterday / etc.)
  const handleRangeChange = (selected) => {
    setDateRange(selected.value);
    setDateError("");

    if (selected.value === "customDate") {
      // keep existing dates; user will pick them
      return;
    }

    const preset = computePresetRange(selected.value);
    if (!preset) return;

    setStartDate(preset.from);
    setEndDate(preset.to);
  };

  // When user changes the DateRangePicker directly
  // `value` is a { start: CalendarDateTime, end: CalendarDateTime } object
  const handleDateRangePickerChange = (value) => {
    setDateRange("customDate");

    if (!value || !value.start || !value.end) {
      setDateError("Please select both a start and end date/time.");
      return;
    }

    const from = calendarDateTimeToDate(value.start);
    const to = calendarDateTimeToDate(value.end);

    if (isNaN(from.getTime()) || isNaN(to.getTime())) {
      setDateError("Invalid date/time format.");
      return;
    }

    if (from > to) {
      setDateError("Start date/time must be before end date/time.");
      return;
    }

    setDateError("");
    setStartDate(from);
    setEndDate(to);
  };

  const handleBusinessChange = (selected) => {
    setBusiness(selected);
    setTerminal(null);
    setTerminalOptions([]);
  };

  // Value object for the picker (controlled)
  const pickerValue = useMemo(() => {
    if (!startDate || !endDate) return null;
    return {
      start: dateToCalendarDateTime(startDate),
      end: dateToCalendarDateTime(endDate),
    };
  }, [startDate, endDate]);

  // ---------- data fetch ----------
  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) return;

    const myId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const payload = {
        start: toLocalIso(startDate),
        finish: toLocalIso(endDate),
        businessGuid: business?.value || null,
        acquirerTId: terminal?.terminal?.acquirerTId || terminal?.value || null,
        // No timezone — backend uses local time
      };
      const data = await getDashboard(payload);
      if (myId !== requestIdRef.current) return;
      setReport(data);
    } catch (err) {
      if (myId !== requestIdRef.current) return;
      setError("Failed to load report. Please try again.");
    } finally {
      if (myId === requestIdRef.current) setLoading(false);
    }
  }, [startDate, endDate, business, terminal]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    const delay = dateRange === "customDate" ? 400 : 0;
    const t = setTimeout(() => fetchReport(), delay);
    return () => clearTimeout(t);
  }, [fetchReport, dateRange, startDate, endDate]);

  const totals = report?.totals || { txnCount: 0, subtotal: 0, total: 0 };
  const fees = report?.fees || [];
  const surcharges = report?.surcharges || [];
  const perTerminal = report?.perTerminal || [];

  // ---------- BUSINESS deep search filter ----------
  const filterBusinessOption = useCallback((option, inputValue) => {
    if (!inputValue || !inputValue.trim()) return true;

    const data = option.data || option;
    const haystack =
      data.__haystack ||
      option.__haystack ||
      normalizeText(data.label || option.label || "");

    const terms = normalizeText(inputValue).split(/\s+/).filter(Boolean);
    return terms.every((term) => haystack.includes(term));
  }, []);

  const formatBusinessLabel = (option) => {
    const mid = option.business?.acquirerMId;
    return mid ? `(${mid}) ${option.label}` : option.label;
  };

  return (
    <Layout
      activeMenu={activeMenu}
      onMenuChange={handleMenuChange}
      title="Dashboard"
    >
      {/* ---------- Filters ---------- */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-4 col-lg-3">
              <label className="form-label small fw-semibold">Date Range</label>
              <Select
                options={rangeOptions}
                value={rangeOptions.find((r) => r.value === dateRange)}
                onChange={handleRangeChange}
                isSearchable={false}
                className="react-select-container small"
                classNamePrefix="react-select"
              />
            </div>

            <div className="col-md-8 col-lg-4">
              <div className="datepicker-wrapper">
                <DateRangePicker
                  label="Select Date & Time Range"
                  granularity="second"
                  hourCycle={24}
                  value={pickerValue}
                  onChange={handleDateRangePickerChange}
                />
                {dateError && (
                  <div className="text-danger small mt-1">{dateError}</div>
                )}
              </div>
            </div>

            {/* ---------- BUSINESS ---------- */}
            <div className="col-md-4 col-lg-3">
              <label className="form-label small fw-semibold">Business</label>
              <Select
                options={businessOptions}
                value={business}
                onChange={handleBusinessChange}
                isLoading={businessLoading}
                isClearable
                isSearchable
                placeholder={
                  businessLoading ? "Loading businesses…" : "Search…"
                }
                className="react-select-container small"
                classNamePrefix="react-select"
                filterOption={filterBusinessOption}
                formatOptionLabel={(option) => formatBusinessLabel(option)}
              />
            </div>

            {/* ---------- TERMINAL ---------- */}
            {business && (
              <div className="col-md-4 col-lg-2">
                <label className="form-label small fw-semibold">Terminal</label>
                <Select
                  options={terminalOptions}
                  value={terminal}
                  onChange={setTerminal}
                  isLoading={terminalLoading}
                  isDisabled={terminalLoading}
                  isClearable
                  isSearchable
                  placeholder={
                    terminalLoading ? "Loading terminals…" : "All Terminals"
                  }
                  className="react-select-container small"
                  classNamePrefix="react-select"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div
          className="alert alert-danger d-flex align-items-center"
          role="alert"
        >
          {error}
        </div>
      )}

      {loading && (
        <div className="text-center py-3">
          <div className="spinner-border text-blaze-orange" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      )}

      {/* ---------- Summary cards ---------- */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-theme-muted small">Transactions</div>
              <div className="h4 mb-0 fw-bold">{fmtInt(totals.txnCount)}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-theme-muted small">Subtotal</div>
              <div className="h4 mb-0 fw-bold">${fmt(totals.subtotal)}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-theme-muted small">Total</div>
              <div className="h4 mb-0 fw-bold">${fmt(totals.total)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Fees & Surcharges ---------- */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Fees</h6>
              {fees.length === 0 ? (
                <div className="text-theme-muted small">
                  No fees for this period.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr className="text-theme-muted small">
                        <th>Type</th>
                        <th className="text-end">Count</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fees.map((f, i) => (
                        <tr key={i}>
                          <td>{f.type}</td>
                          <td className="text-end">{fmtInt(f.cnt)}</td>
                          <td className="text-end">${fmt(f.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Surcharges</h6>
              {surcharges.length === 0 ? (
                <div className="text-theme-muted small">
                  No surcharges for this period.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr className="text-theme-muted small">
                        <th>Type</th>
                        <th className="text-end">Count</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {surcharges.map((s, i) => (
                        <tr key={i}>
                          <td>{s.type}</td>
                          <td className="text-end">{fmtInt(s.cnt)}</td>
                          <td className="text-end">${fmt(s.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Per Terminal ---------- */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold mb-0">Per Terminal Breakdown</h6>
          </div>

          {perTerminal.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-theme-muted mt-3 mb-0">
                No per-terminal data available. Select a{" "}
                <strong>Business</strong> or <strong>Terminal</strong> to view
                the breakdown.
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr className="small text-theme-muted">
                    <th>TID</th>
                    <th className="text-end">Txns</th>
                    <th className="text-end">Subtotal</th>
                    <th className="text-end">Fees</th>
                    <th className="text-end">Surcharges</th>
                    {/* <th className="text-end">Cashout</th>
                    <th className="text-end">Tips</th> */}
                    <th className="text-end fw-bold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {perTerminal.map((t, i) => (
                    <tr key={i}>
                      <td className="fw-semibold">{t.acquirerTId}</td>
                      <td className="text-end">{fmtInt(t.txnCount)}</td>
                      <td className="text-end">${fmt(t.subtotal)}</td>
                      <td className="text-end">${fmt(t.feeTotal)}</td>
                      <td className="text-end">${fmt(t.surchargeTotal)}</td>
                      {/* <td className="text-end">${fmt(t.cashoutTotal)}</td>
                      <td className="text-end">${fmt(t.tipsTotal)}</td> */}
                      <td className="text-end fw-bold text-blaze-orange">
                        ${fmt(t.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light fw-bold">
                  <tr>
                    <td>Total</td>
                    <td className="text-end">{fmtInt(totals.txnCount)}</td>
                    <td className="text-end">${fmt(totals.subtotal)}</td>
                    <td className="text-end">
                      $
                      {fmt(
                        perTerminal.reduce((s, t) => s + (t.feeTotal || 0), 0),
                      )}
                    </td>
                    <td className="text-end">
                      $
                      {fmt(
                        perTerminal.reduce(
                          (s, t) => s + (t.surchargeTotal || 0),
                          0,
                        ),
                      )}
                    </td>
                    {/* <td className="text-end">
                      $
                      {fmt(
                        perTerminal.reduce(
                          (s, t) => s + (t.cashoutTotal || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="text-end">
                      $
                      {fmt(
                        perTerminal.reduce((s, t) => s + (t.tipsTotal || 0), 0),
                      )}
                    </td> */}
                    <td className="text-end text-blaze-orange">
                      ${fmt(totals.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
